// src/pages/Register.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User,
  Lock,
  Eye,
  EyeOff,
  Mail,
  FileText,
  Loader,
  ArrowRight,
  Stethoscope,
  Users,
  AlertCircle,
} from 'lucide-react';
import api from '../services/api';
import {
  validate,
  isRequired,
  isValidEmail,
  matchesField,
  isValidPassword,
  ValidationSchema,
} from '../utils/validation';

type UserType = 'doctor' | 'public';

// ── Validation Schemas ──────────────────────────────────────────────
const doctorSchema: ValidationSchema = {
  name: [isRequired('Full name')],
  email: [isRequired('Email'), isValidEmail],
  medicalLicenceNumber: [isRequired('Medical licence number')],
  password: [isRequired('Password'), isValidPassword],
  confirmPassword: [isRequired('Confirm password'), matchesField('password', 'Confirm password')],
};

const publicSchema: ValidationSchema = {
  firstName: [isRequired('First name')],
  lastName: [isRequired('Last name')],
  email: [isRequired('Email'), isValidEmail],
  password: [isRequired('Password'), isValidPassword],
  confirmPassword: [isRequired('Confirm password'), matchesField('password', 'Confirm password')],
};

const Register: React.FC = () => {
  const navigate = useNavigate();

  // ── User Type Toggle ──────────────────────────────────────────────
  const [userType, setUserType] = useState<UserType>('doctor');

  // ── Doctor Form Fields ────────────────────────────────────────────
  const [name, setName] = useState('');
  const [medicalLicenceNumber, setMedicalLicenceNumber] = useState('');

  // ── Public Form Fields ────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // ── Shared Fields ─────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Form State ───────────────────────────────────────────────────
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const currentSchema = userType === 'doctor' ? doctorSchema : publicSchema;

  const fieldValues = (overrides: Record<string, string> = {}) => {
    if (userType === 'doctor') {
      return { name, email, medicalLicenceNumber, password, confirmPassword, ...overrides };
    }
    return { firstName, lastName, email, password, confirmPassword, ...overrides };
  };

  const validateField = (field: string, values = fieldValues()) => {
    return validate({ [field]: currentSchema[field] }, values);
  };

  const applyFieldErrors = (fieldErrors: Record<string, string>, fields: string[]) => {
    setErrors((prev) => {
      const next = { ...prev };
      for (const f of fields) {
        if (fieldErrors[f]) next[f] = fieldErrors[f];
        else delete next[f];
      }
      return next;
    });
  };

  const handleChange = (field: string, setter: (v: string) => void, value: string) => {
    setter(value);
    if (!touched[field]) return;
    const values = fieldValues({ [field]: value });
    applyFieldErrors(validateField(field, values), [field]);
    if (field === 'password' && touched.confirmPassword) {
      applyFieldErrors(validateField('confirmPassword', values), ['confirmPassword']);
    }
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const values = fieldValues();
    applyFieldErrors(validateField(field, values), [field]);
    if (field === 'password' && touched.confirmPassword) {
      applyFieldErrors(validateField('confirmPassword', values), ['confirmPassword']);
    }
  };

  const validateAll = () => {
    const allErrors = validate(currentSchema, fieldValues());
    setErrors(allErrors);
    const allTouched: Record<string, boolean> = {};
    Object.keys(currentSchema).forEach((k) => (allTouched[k] = true));
    setTouched(allTouched);
    return Object.keys(allErrors).length === 0;
  };

  const handleUserTypeSwitch = (type: UserType) => {
    if (type === userType) return;
    setUserType(type);
    setErrors({});
    setTouched({});
    setApiError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validateAll()) return;

    if (userType === 'public' && !disclaimerAccepted) {
      setApiError('You must accept the informational use disclaimer to proceed.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (userType === 'doctor') {
        const response = await api.post("/doctor/register/send-otp", {
          name,
          email,
          medicalLicenseNumber: medicalLicenceNumber,
          password,
          confirmPassword,
        });

        if (response.data?.success || response.status === 200) {
          navigate('/register/verify-otp', {
            state: {
              userType: 'doctor',
              email,
              devOtp: response.data?.data?.otp,
            },
          });
        }
      } else {
        const response = await api.post("/public/register/send-otp", {
          firstName,
          lastName,
          email,
          password,
          confirmPassword,
          disclaimerAccepted,
        });

        if (response.data?.success || response.status === 200) {
          navigate('/register/verify-otp', {
            state: {
              userType: 'public',
              email,
              devOtp: response.data?.data?.otp,
            },
          });
        }
      }
    } catch (err: any) {
      console.error("Get OTP error:", err);
      let errorMessage = "Failed to send verification code. Please check your details.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        if (errorMessage.toLowerCase().includes("email")) {
          setErrors((prev) => ({ ...prev, email: errorMessage }));
          setTouched((prev) => ({ ...prev, email: true }));
        } else if (
          errorMessage.toLowerCase().includes("license") ||
          errorMessage.toLowerCase().includes("licence")
        ) {
          setErrors((prev) => ({ ...prev, medicalLicenceNumber: errorMessage }));
          setTouched((prev) => ({ ...prev, medicalLicenceNumber: true }));
        } else {
          setApiError(errorMessage);
        }
      } else {
        setApiError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasError = (field: string) => !!errors[field] && !!touched[field];
  const fieldError = (field: string) =>
    hasError(field) ? (
      <p className="mt-1 text-xs text-red-100 font-medium">{errors[field]}</p>
    ) : null;

  const inputClass = (field: string, extra = '') =>
    `w-full h-[40px] sm:h-[42px] bg-white rounded-md pl-10 pr-3.5 text-[14px] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm transition-all border ${
      hasError(field)
        ? 'border-red-300 focus:ring-2 focus:ring-red-400'
        : 'border-white/40 focus:ring-2 focus:ring-[#097173]'
    } ${extra}`;

  return (
    <div className="relative w-screen min-h-screen md:h-screen flex flex-col md:flex-row overflow-x-hidden md:overflow-hidden bg-white">
      {/* Asymmetric Curved Divider SVG (desktop) */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-[1] hidden md:block" aria-hidden="true">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="regTealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#26ABB0" />
              <stop offset="100%" stopColor="#219FA4" />
            </linearGradient>
          </defs>
          <path
            d="M 526.3 0 C 464.5 333.3, 475.4 666.7, 602.3 1000 L 1000 1000 L 1000 0 Z"
            fill="url(#regTealGrad)"
          />
        </svg>
      </div>

      {/* LEFT PANEL: Branding & Decorative Graphics */}
      <div className="relative w-full md:w-1/2 h-auto md:h-full flex flex-col justify-between px-6 py-3 sm:p-11 lg:p-14 z-[2] bg-white md:bg-transparent shrink-0">
        <div className="my-2 sm:my-4 md:my-0 md:absolute md:top-[43%] md:left-[50%] md:-translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center text-center w-full max-w-[90%] md:max-w-[720px] lg:max-w-[820px] xl:max-w-[880px] px-2">
          <img
            src="/logo/DiagnomIQ-01.svg"
            alt="DiagnomIQ - Precision insights for every patient"
            className="w-full max-w-[200px] sm:max-w-[460px] md:max-w-[640px] lg:max-w-[740px] xl:max-w-[800px] h-auto object-contain"
          />
        </div>

        {/* Decorative Medical Graphic (Bottom-Left) */}
        <svg
          className="hidden md:block absolute bottom-0 left-0 w-auto h-[48vh] max-h-[500px] pointer-events-none z-[1]"
          viewBox="0 0 380 500"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M 90 -20 L 126 15 Q 140 29 154 43 L 265 154 Q 283 172 283 198 L 283 310 Q 283 336 265 354 L 154 465 Q 140 479 126 493 L 90 528"
            stroke="#D2EEF1"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path
            d="M 0 57 L 61 57 Q 78 57 90 69 L 204 183 Q 222 201 222 225 L 222 297 Q 222 321 204 339 L 90 453 Q 78 465 61 465 L 0 465 Z"
            fill="#EDFAFB"
          />
          <path
            d="M 0 133 L 46 133 Q 67 133 67 154 L 67 216 Q 67 234 85 234 L 138 234 Q 158 234 158 254 L 158 284 Q 158 304 138 304 L 85 304 Q 67 304 67 322 L 67 384 Q 67 405 46 405 L 0 405 Z"
            fill="#FFFFFF"
          />
        </svg>
      </div>

      {/* RIGHT PANEL: Form with Teal Background (no visible scrollbar) */}
      <div className="relative w-full md:w-1/2 flex-1 md:h-full overflow-y-auto z-[2] bg-[#23A4A8] md:bg-transparent rounded-t-[32px] md:rounded-none shadow-lg md:shadow-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {/* Top-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute top-0 right-0 w-[300px] lg:w-[420px] h-[300px] lg:h-[420px] pointer-events-none z-[1]"
          viewBox="0 0 450 450"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="492" cy="-25" r="225" fill="#2FAFB1" />
          <circle cx="492" cy="-25" r="162" fill="#48B9BC" />
        </svg>

        {/* Bottom-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute bottom-0 right-0 w-[340px] lg:w-[500px] h-[340px] lg:h-[500px] pointer-events-none z-[1]"
          viewBox="0 0 550 550"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="578" cy="651" r="298" fill="#33AAAD" />
          <circle cx="578" cy="651" r="244" fill="#44B6B9" />
        </svg>

        {/* Inner Content Centered Horizontally & Vertically */}
        <div className="min-h-full w-full flex flex-col justify-center items-center px-5 sm:px-8 lg:px-12 py-5 sm:py-6">
          <div className="relative z-[3] w-full max-w-[460px]">
            <h1 className="text-[22px] sm:text-[25px] font-bold text-white tracking-tight leading-tight mb-1">
              Create Account
            </h1>
            <p className="text-[13px] sm:text-[13.5px] font-normal text-[#E0F7F6] mb-3 leading-relaxed">
              Join DiagnomIQ for precision-powered insights
            </p>

            {/* ── User Type Toggle ─────────────────────────────────── */}
            <div className="flex rounded-lg overflow-hidden mb-3.5 bg-white/15 border border-white/25 p-1 gap-1">
              <button
                type="button"
                onClick={() => handleUserTypeSwitch('doctor')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-2.5 rounded-md text-[13px] sm:text-[13.5px] font-semibold transition-all duration-200 cursor-pointer ${
                  userType === 'doctor'
                    ? 'bg-white text-[#097173] shadow-sm'
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                <Stethoscope className="h-4 w-4 shrink-0" />
                <span className="truncate">Medical Professional</span>
              </button>
              <button
                type="button"
                onClick={() => handleUserTypeSwitch('public')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-2.5 rounded-md text-[13px] sm:text-[13.5px] font-semibold transition-all duration-200 cursor-pointer ${
                  userType === 'public'
                    ? 'bg-white text-[#097173] shadow-sm'
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">Patient / Public</span>
              </button>
            </div>

            {/* ── API Error Banner ──────────────────────────────────── */}
            {apiError && (
              <div className="flex items-center space-x-2 text-white bg-red-600/40 border border-red-300/40 p-2.5 rounded-md text-xs sm:text-sm mb-3.5">
                <AlertCircle className="h-4.5 w-4.5 shrink-0 text-red-200" />
                <span>{apiError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-2.5" noValidate>
              {userType === 'doctor' ? (
                /* ── Doctor Registration Fields ────────────────────── */
                <>
                  {/* Full Name */}
                  <div>
                    <label htmlFor="name" className="block text-[13px] font-semibold text-white mb-1">
                      Full Name
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                        <User className="h-4 w-4" />
                      </span>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        value={name}
                        onChange={(e) => handleChange('name', setName, e.target.value)}
                        onBlur={() => handleBlur('name')}
                        className={inputClass('name')}
                        placeholder="Dr. John Smith"
                      />
                    </div>
                    {fieldError('name')}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-[13px] font-semibold text-white mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                        <Mail className="h-4 w-4" />
                      </span>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => handleChange('email', setEmail, e.target.value)}
                        onBlur={() => handleBlur('email')}
                        className={inputClass('email')}
                        placeholder="doctor@clinic.com"
                      />
                    </div>
                    {fieldError('email')}
                  </div>

                  {/* Medical Licence Number */}
                  <div>
                    <label htmlFor="medicalLicenceNumber" className="block text-[13px] font-semibold text-white mb-1">
                      Medical Licence Number
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                        <FileText className="h-4 w-4" />
                      </span>
                      <input
                        id="medicalLicenceNumber"
                        name="medicalLicenceNumber"
                        type="text"
                        required
                        value={medicalLicenceNumber}
                        onChange={(e) => handleChange('medicalLicenceNumber', setMedicalLicenceNumber, e.target.value)}
                        onBlur={() => handleBlur('medicalLicenceNumber')}
                        className={inputClass('medicalLicenceNumber')}
                        placeholder="e.g. GMC1234567"
                      />
                    </div>
                    {fieldError('medicalLicenceNumber')}
                  </div>

                  {/* Specialization, licensing and qualification details are
                      collected on the next step, after email verification. */}
                </>
              ) : (
                /* ── Public User Registration Fields ───────────────── */
                <>
                  {/* First & Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div>
                      <label htmlFor="firstName" className="block text-[13px] font-semibold text-white mb-1">
                        First Name
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                          <User className="h-4 w-4" />
                        </span>
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          autoComplete="given-name"
                          required
                          value={firstName}
                          onChange={(e) => handleChange('firstName', setFirstName, e.target.value)}
                          onBlur={() => handleBlur('firstName')}
                          className={inputClass('firstName')}
                          placeholder="John"
                        />
                      </div>
                      {fieldError('firstName')}
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-[13px] font-semibold text-white mb-1">
                        Last Name
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                          <User className="h-4 w-4" />
                        </span>
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          autoComplete="family-name"
                          required
                          value={lastName}
                          onChange={(e) => handleChange('lastName', setLastName, e.target.value)}
                          onBlur={() => handleBlur('lastName')}
                          className={inputClass('lastName')}
                          placeholder="Smith"
                        />
                      </div>
                      {fieldError('lastName')}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="publicEmail" className="block text-[13px] font-semibold text-white mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                        <Mail className="h-4 w-4" />
                      </span>
                      <input
                        id="publicEmail"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => handleChange('email', setEmail, e.target.value)}
                        onBlur={() => handleBlur('email')}
                        className={inputClass('email')}
                        placeholder="your@email.com"
                      />
                    </div>
                    {fieldError('email')}
                  </div>

                </>
              )}

              {/* ── Password Fields (shared) ────────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label htmlFor="password" className="block text-[13px] font-semibold text-white mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                      <Lock className="h-4 w-4" />
                    </span>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => handleChange('password', setPassword, e.target.value)}
                      onBlur={() => handleBlur('password')}
                      className={inputClass('password', 'pr-9')}
                      placeholder="Create password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] focus:outline-none p-1 transition-colors cursor-pointer"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {fieldError('password')}
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-[13px] font-semibold text-white mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                      <Lock className="h-4 w-4" />
                    </span>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', setConfirmPassword, e.target.value)}
                      onBlur={() => handleBlur('confirmPassword')}
                      className={inputClass('confirmPassword', 'pr-9')}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] focus:outline-none p-1 transition-colors cursor-pointer"
                      aria-label="Toggle confirm password visibility"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {fieldError('confirmPassword')}
                </div>
              </div>

              {/* ── Public User Disclaimer Checkbox ─────────────────── */}
              {userType === 'public' && (
                <div
                  className={`flex items-start gap-2.5 p-2.5 rounded-lg border transition-colors ${
                    disclaimerAccepted
                      ? 'bg-white/15 border-white/30'
                      : 'bg-white/5 border-white/15'
                  }`}
                >
                  <input
                    type="checkbox"
                    id="disclaimer"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-white/40 text-[#097173] focus:ring-[#097173] cursor-pointer accent-[#097173]"
                  />
                  <label htmlFor="disclaimer" className="text-[12px] sm:text-[12.5px] text-white/90 leading-relaxed cursor-pointer select-none">
                    I acknowledge that DiagnomIQ MDT reports are generated for{' '}
                    <strong className="text-white">informational purposes only</strong> and do not replace
                    professional clinical diagnosis or medical advice. I accept the{' '}
                    <Link to="/legal?tab=terms-and-conditions" className="underline text-white hover:text-[#D8F2F1]">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/legal?tab=medical-disclaimer" className="underline text-white hover:text-[#D8F2F1]">Medical Disclaimer</Link>.
                  </label>
                </div>
              )}

              {/* ── Submit Button ────────────────────────────────────── */}
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={isSubmitting || (userType === 'public' && !disclaimerAccepted)}
                  aria-label="Get OTP to verify your email"
                  className="w-full h-[42px] sm:h-[44px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[14.5px] sm:text-[15px] rounded-md flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="h-4.5 w-4.5 mr-2 animate-spin" />
                      <span>Sending OTP...</span>
                    </>
                  ) : (
                    <>
                      <span>Get OTP</span>
                      <ArrowRight className="h-4.5 w-4.5 ml-1" />
                    </>
                  )}
                </button>
              </div>

              {/* ── Footer links ────────────────────────────────────── */}
              <div className="text-center space-y-2 pt-1">
                {userType === 'doctor' && (
                  <p className="text-[11.5px] sm:text-[12px] text-white/75 bg-white/10 border border-white/15 rounded-md px-2.5 py-1.5 leading-snug">
                    You will be asked to review and accept our Terms, Privacy
                    Policy and Medical Disclaimer once, when you first sign in.
                  </p>
                )}

                <p className="text-[13.5px] font-medium text-white/95">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="font-bold text-white underline hover:text-[#D8F2F1] transition-colors ml-1"
                  >
                    Sign in
                  </Link>
                </p>

                <div className="pt-2 border-t border-white/20 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] font-medium text-white/80">
                  <Link
                    to="/legal?tab=terms-and-conditions"
                    className="hover:text-white hover:underline transition-colors"
                  >
                    Terms
                  </Link>
                  <span className="text-white/50 text-xs select-none">&bull;</span>
                  <Link
                    to="/legal?tab=privacy-policy"
                    className="hover:text-white hover:underline transition-colors"
                  >
                    Privacy
                  </Link>
                  <span className="text-white/50 text-xs select-none">&bull;</span>
                  <Link
                    to="/legal?tab=medical-disclaimer"
                    className="hover:text-white hover:underline transition-colors"
                  >
                    Medical Disclaimer
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;